import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import employmentHistoryService from '../services/employmentHistoryService';
import styles from './AddEmploymentModal.module.css';

const AddEmploymentModal = ({ isOpen, onClose, guardianId, onSuccess }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    job_title: '',
    date_start: '',
    date_end: '',
    still_in_employment: 0,
    address_1: '',
    address_2: '',
    address_3: '',
    town: '',
    postcode: '',
    referee_name: '',
    referee_telephone: '',
    referee_email: '',
    reason_for_living: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Re-validate dates when they change to fix stale state issues
  useEffect(() => {
    if (formData.date_start && formData.date_end && parseInt(formData.still_in_employment) === 0) {
      const startDate = new Date(formData.date_start);
      const endDate = new Date(formData.date_end);
      
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        if (endDate <= startDate) {
          newErrors.date_end = 'End date must be after start date';
        } else if (newErrors.date_end === 'End date must be after start date') {
          delete newErrors.date_end;
        }
        return newErrors;
      });
    }
  }, [formData.date_start, formData.date_end, formData.still_in_employment]);

  const validateForm = () => {
    const newErrors = {};

    // Required fields matching Laravel validation
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }

    if (!formData.job_title.trim()) {
      newErrors.job_title = 'Job title is required';
    }

    if (!formData.address_1.trim()) {
      newErrors.address_1 = 'Address 1 is required';
    }

    if (!formData.town.trim()) {
      newErrors.town = 'Town is required';
    }

    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required';
    }

    if (!formData.date_start) {
      newErrors.date_start = 'Start date is required';
    }

    if (parseInt(formData.still_in_employment) === 0 && !formData.date_end) {
      newErrors.date_end = 'End date is required when not currently employed';
    }

    if (formData.date_start && formData.date_end && parseInt(formData.still_in_employment) === 0) {
      const startDate = new Date(formData.date_start);
      const endDate = new Date(formData.date_end);
      if (endDate <= startDate) {
        newErrors.date_end = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    setLoading(true);

    try {
      const dataToSubmit = {
        ...formData,
        guardian_id: guardianId,
        // Convert still_in_employment to 0/1 for backend
        still_in_employment: parseInt(formData.still_in_employment) === 1 ? 1 : 0,
        // If still employed, set date_end to null
        date_end: parseInt(formData.still_in_employment) === 1 ? null : formData.date_end,
      };

      await employmentHistoryService.create(dataToSubmit);
      toast.success('Employment history added successfully');

      // Reset form with all fields
      setFormData({
        company_name: '',
        job_title: '',
        date_start: '',
        date_end: '',
        still_in_employment: 0,
        address_1: '',
        address_2: '',
        address_3: '',
        town: '',
        postcode: '',
        referee_name: '',
        referee_telephone: '',
        referee_email: '',
        reason_for_living: '',
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error adding employment history:', error);
      toast.error(error.response?.data?.message || 'Failed to add employment history');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prevData) => {
      const newData = { ...prevData, [field]: value };
      
      // Clear error for this field when user starts typing
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        if (newErrors[field]) {
          delete newErrors[field];
        }
        return newErrors;
      });
      
      return newData;
    });
  };

  const handleBlur = (field, value) => {
    // Validate individual field on blur for better UX
    // Sync formData with actual input value (in case it was set via JavaScript)
    // Then validate using the synced value
    setFormData((currentFormData) => {
      const syncedData = { ...currentFormData, [field]: value };
      
      // Validate using synced data
      setErrors((currentErrors) => {
        const newErrors = { ...currentErrors };
        
        if (field === 'date_start') {
          if (!value || (typeof value === 'string' && !value.trim())) {
            newErrors.date_start = 'Start date is required';
          } else {
            delete newErrors.date_start;
            // Re-validate date_end if it exists
            if (syncedData.date_end && parseInt(syncedData.still_in_employment) === 0) {
              const startDate = new Date(value);
              const endDate = new Date(syncedData.date_end);
              if (endDate <= startDate) {
                newErrors.date_end = 'End date must be after start date';
              } else if (newErrors.date_end === 'End date must be after start date') {
                delete newErrors.date_end;
              }
            }
          }
        }
        
        if (field === 'date_end') {
          if (parseInt(syncedData.still_in_employment) === 0 && (!value || (typeof value === 'string' && !value.trim()))) {
            newErrors.date_end = 'End date is required when not currently employed';
          } else if (value && syncedData.date_start) {
            // Check if end date is after start date using synced values
            const startDate = new Date(syncedData.date_start);
            const endDate = new Date(value);
            if (endDate <= startDate) {
              newErrors.date_end = 'End date must be after start date';
            } else {
              delete newErrors.date_end;
            }
          } else if (value) {
            delete newErrors.date_end;
          }
        }
      
        if (field === 'company_name') {
          if (!value || !value.trim()) {
            newErrors.company_name = 'Company name is required';
          } else {
            delete newErrors.company_name;
          }
        }
        
        if (field === 'address_1') {
          if (!value || !value.trim()) {
            newErrors.address_1 = 'Address 1 is required';
          } else {
            delete newErrors.address_1;
          }
        }
        
        if (field === 'town') {
          if (!value || !value.trim()) {
            newErrors.town = 'Town is required';
          } else {
            delete newErrors.town;
          }
        }
        
        if (field === 'postcode') {
          if (!value || !value.trim()) {
            newErrors.postcode = 'Postcode is required';
          } else {
            delete newErrors.postcode;
          }
        }
        
        if (field === 'job_title') {
          if (!value || !value.trim()) {
            newErrors.job_title = 'Job title is required';
          } else {
            delete newErrors.job_title;
          }
        }
        
        return newErrors;
      });
      
      return syncedData;
    });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h4 className={styles.modalTitle}>Add employment history</h4>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div className={styles.modalBody}>
          <form onSubmit={handleSubmit} id="employment-history-form">
            <div className={styles.formGroup}>
              <label htmlFor="date_start">Date start <span className={styles.required}>*</span></label>
              <input
                type="date"
                id="date_start"
                className={`${styles.formControl} ${errors.date_start ? styles.isInvalid : ''}`}
                value={formData.date_start}
                onChange={(e) => handleChange('date_start', e.target.value)}
                onInput={(e) => handleChange('date_start', e.target.value)}
                onBlur={(e) => handleBlur('date_start', e.target.value)}
              />
              {errors.date_start && (
                <div className={styles.invalidFeedback}>{errors.date_start}</div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="still_in_employment">Still in employment <span className={styles.required}>*</span></label>
              <select
                id="still_in_employment"
                className={styles.formControl}
                value={formData.still_in_employment}
                onChange={(e) => {
                  handleChange('still_in_employment', e.target.value);
                  // Clear date_end when still employed
                  if (parseInt(e.target.value) === 1) {
                    handleChange('date_end', '');
                  }
                }}
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </div>

            {parseInt(formData.still_in_employment) === 0 && (
              <div className={styles.formGroup}>
                <label htmlFor="date_end">Date end <span className={styles.required}>*</span></label>
                <input
                  type="date"
                  id="date_end"
                  className={`${styles.formControl} ${errors.date_end ? styles.isInvalid : ''}`}
                  value={formData.date_end}
                  onChange={(e) => handleChange('date_end', e.target.value)}
                  onInput={(e) => handleChange('date_end', e.target.value)}
                  onBlur={(e) => handleBlur('date_end', e.target.value)}
                />
                {errors.date_end && (
                  <div className={styles.invalidFeedback}>{errors.date_end}</div>
                )}
              </div>
            )}

            <div className={styles.formGroup}>
              <label htmlFor="company_name">Company name <span className={styles.required}>*</span></label>
              <input
                type="text"
                id="company_name"
                className={`${styles.formControl} ${errors.company_name ? styles.isInvalid : ''}`}
                value={formData.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                onBlur={(e) => handleBlur('company_name', e.target.value)}
                placeholder="Enter company name"
              />
              {errors.company_name && (
                <div className={styles.invalidFeedback}>{errors.company_name}</div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="address_1">Address 1 <span className={styles.required}>*</span></label>
              <input
                type="text"
                id="address_1"
                className={`${styles.formControl} ${errors.address_1 ? styles.isInvalid : ''}`}
                value={formData.address_1}
                onChange={(e) => handleChange('address_1', e.target.value)}
                onBlur={(e) => handleBlur('address_1', e.target.value)}
                placeholder="Enter address line 1"
              />
              {errors.address_1 && (
                <div className={styles.invalidFeedback}>{errors.address_1}</div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="address_2">Address 2</label>
              <input
                type="text"
                id="address_2"
                className={styles.formControl}
                value={formData.address_2}
                onChange={(e) => handleChange('address_2', e.target.value)}
                placeholder="Enter address line 2 (optional)"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="address_3">Address 3</label>
              <input
                type="text"
                id="address_3"
                className={styles.formControl}
                value={formData.address_3}
                onChange={(e) => handleChange('address_3', e.target.value)}
                placeholder="Enter address line 3 (optional)"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="town">Town <span className={styles.required}>*</span></label>
              <input
                type="text"
                id="town"
                className={`${styles.formControl} ${errors.town ? styles.isInvalid : ''}`}
                value={formData.town}
                onChange={(e) => handleChange('town', e.target.value)}
                onBlur={(e) => handleBlur('town', e.target.value)}
                placeholder="Enter town"
              />
              {errors.town && (
                <div className={styles.invalidFeedback}>{errors.town}</div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="postcode">Postcode <span className={styles.required}>*</span></label>
              <input
                type="text"
                id="postcode"
                className={`${styles.formControl} ${errors.postcode ? styles.isInvalid : ''}`}
                value={formData.postcode}
                onChange={(e) => handleChange('postcode', e.target.value)}
                onBlur={(e) => handleBlur('postcode', e.target.value)}
                placeholder="Enter postcode"
              />
              {errors.postcode && (
                <div className={styles.invalidFeedback}>{errors.postcode}</div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="referee_name">Referee name</label>
              <input
                type="text"
                id="referee_name"
                className={styles.formControl}
                value={formData.referee_name}
                onChange={(e) => handleChange('referee_name', e.target.value)}
                placeholder="Enter referee name (optional)"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="referee_telephone">Referee telephone</label>
              <input
                type="text"
                id="referee_telephone"
                className={styles.formControl}
                value={formData.referee_telephone}
                onChange={(e) => handleChange('referee_telephone', e.target.value)}
                placeholder="Enter referee telephone (optional)"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="referee_email">Referee email</label>
              <input
                type="email"
                id="referee_email"
                className={styles.formControl}
                value={formData.referee_email}
                onChange={(e) => handleChange('referee_email', e.target.value)}
                placeholder="Enter referee email (optional)"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="job_title">Job title <span className={styles.required}>*</span></label>
              <input
                type="text"
                id="job_title"
                className={`${styles.formControl} ${errors.job_title ? styles.isInvalid : ''}`}
                value={formData.job_title}
                onChange={(e) => handleChange('job_title', e.target.value)}
                onBlur={(e) => handleBlur('job_title', e.target.value)}
                placeholder="Enter job title"
              />
              {errors.job_title && (
                <div className={styles.invalidFeedback}>{errors.job_title}</div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="reason_for_living">Reason for leaving</label>
              <textarea
                id="reason_for_living"
                className={styles.formControl}
                value={formData.reason_for_living}
                onChange={(e) => handleChange('reason_for_living', e.target.value)}
                placeholder="Enter reason for leaving (optional)"
                rows="3"
              />
            </div>

            <div className={styles.formActions}>
              <button
                type="submit"
                className={`${styles.btnPrimary} ${loading ? styles.disabled : ''}`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className={styles.spinner}></span> Adding...
                  </>
                ) : (
                  <>
                    <i className="glyphicon glyphicon-plus"></i> Add to employment history
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={onClose}
          >
            Close without saving
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEmploymentModal;
